import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import ProLayout from '@/components/layouts/ProLayout';
import { 
  LayoutDashboard, Users, GraduationCap, 
  ChevronRight, TrendingUp, Clock, Star, BookOpen, Video
} from 'lucide-react';

const TutorDashboard = () => {
  const { user, api } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  const sidebarItems = [
    { path: '/tutor', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/tutoring', label: 'Live Sessions', icon: Video },
  ];

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const response = await api.get('/v2/tutor/my-students');
        setStudents(response.data.students);
      } catch (error) {
        console.error('Error fetching students:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStudents();
  }, [api]);

  const keyStageLabels = {
    ks1: 'KS1',
    ks2: 'KS2',
    ks3: 'KS3',
    ks4: 'KS4',
    esol: 'ESOL',
    '11plus': '11+'
  };

  return (
    <ProLayout title="Tutor Dashboard" sidebarItems={sidebarItems}>
      <div data-testid="tutor-dashboard">
        {/* Welcome Card */}
        <div className="pro-card bg-gradient-to-r from-purple-500 to-indigo-600 text-white mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold mb-2">
                Welcome back, {user?.first_name}!
              </h1>
              <p className="text-purple-100">
                You have {students.length} student{students.length !== 1 ? 's' : ''} assigned to you
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                <Users className="w-8 h-8 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="pro-card text-center">
            <GraduationCap className="w-8 h-8 text-purple-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-slate-800">{students.length}</p>
            <p className="text-sm text-slate-500">Students</p>
          </div>
          <div className="pro-card text-center">
            <BookOpen className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-slate-800">
              {students.reduce((sum, s) => sum + (s.lessons_completed || 0), 0)}
            </p>
            <p className="text-sm text-slate-500">Lessons Completed</p>
          </div>
          <div className="pro-card text-center">
            <Star className="w-8 h-8 text-amber-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-slate-800">
              {students.reduce((sum, s) => sum + (s.total_points || 0), 0)}
            </p>
            <p className="text-sm text-slate-500">Total Points</p>
          </div>
          <div className="pro-card text-center">
            <TrendingUp className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-slate-800">
              {students.filter(s => s.lessons_completed > 0).length}
            </p>
            <p className="text-sm text-slate-500">Active Students</p>
          </div>
        </div>

        {/* Students List */}
        <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">Your Students</h2>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-600 border-t-transparent"></div>
          </div>
        ) : students.length === 0 ? (
          <div className="pro-card text-center py-12">
            <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-bold text-slate-700 mb-2">
              No Students Assigned
            </h3>
            <p className="text-slate-500">
              Contact your administrator to have students assigned to you
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {students.map((student) => (
              <Link 
                key={student.id} 
                to={`/tutor/student/${student.id}`}
                data-testid={`student-card-${student.id}`}
              >
                <div className="pro-card group cursor-pointer hover:border-purple-500 border-2 border-transparent transition-all">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                      <span className="font-bold text-purple-700">
                        {student.first_name?.charAt(0)}
                      </span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-800 group-hover:text-purple-700 transition-colors">
                        {student.first_name} {student.last_name}
                      </h3>
                      <p className="text-sm text-slate-500">
                        Year {student.year_group} • {keyStageLabels[student.key_stage]}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-slate-50 rounded-lg p-2">
                      <p className="text-lg font-bold text-purple-600">{student.lessons_completed || 0}</p>
                      <p className="text-xs text-slate-500">Lessons</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2">
                      <p className="text-lg font-bold text-amber-600">{student.total_points || 0}</p>
                      <p className="text-xs text-slate-500">Points</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-2">
                      <p className="text-lg font-bold text-emerald-600">{student.total_stars || 0}</p>
                      <p className="text-xs text-slate-500">Stars</p>
                    </div>
                  </div>

                  {/* Subjects */}
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex flex-wrap gap-1">
                      {(student.allocated_subjects || []).map(subj => (
                        <span 
                          key={subj}
                          className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full capitalize"
                        >
                          {subj}
                        </span>
                      ))}
                      {(!student.allocated_subjects || student.allocated_subjects.length === 0) && (
                        <span className="text-xs text-slate-400">No subjects assigned</span>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </ProLayout>
  );
};

export default TutorDashboard;
